import { regionalCeraSubsetFilesPresentInTree, notoScriptSupplementFilesPresentInTree, notoCjkStylesheetPresentInTree } from "ll-regional-font-sentinel";
import {
  UNICODE_RANGE_REGIONAL_CYRILLIC,
  UNICODE_RANGE_REGIONAL_GREEK,
  UNICODE_RANGE_REGIONAL_LATIN,
  UNICODE_RANGE_REGIONAL_VIETNAMESE,
  ceraRegionalSupplementKeyForUiLang,
} from "./cera-regional-scripts.js";
import {
  notoScriptSupplementFontFaceRows,
  notoSansUiFontFaceRows,
} from "./noto-script-supplement.js";
import { appendDevMediaCacheBuster } from "./media-cache-buster.js";
import { versionPublicAssetUrl } from "./site-asset-version.js";

/**
 * HTTPS pages block http:// subresources (fonts, audio, …). In production, force https on
 * configured bases and absolute URLs so a mistaken `http://` env value still works.
 */
function upgradeHttpToHttpsInProduction(url) {
  const env = globalThis.process?.env;
  if (env?.NODE_ENV !== "production") return url;
  if (typeof url !== "string" || !url.startsWith("http://")) return url;
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    if (h === "localhost" || h === "127.0.0.1") return url;
  } catch {
    /* ignore */
  }
  return `https://${url.slice("http://".length)}`;
}

/**
 * Canonical, `og:url`, `hreflang`: avoid `http://` on HTTPS sites when the app sits
 * behind a reverse proxy that forwards requests as HTTP (use `TRUST_PROXY=1` too).
 * @param {string} urlString
 * @returns {string}
 */
export function normalizePageAbsoluteUrl(urlString) {
  return upgradeHttpToHttpsInProduction(String(urlString || "").trim());
}

/**
 * Public base URL for static prerender (Marko crawler uses `http://localhost/` → wrong canonical /
 * og:url). Set at **build** time (`SITE_ORIGIN` or alias `PUBLIC_SITE_ORIGIN`): `https://beta.lernlaterne.de`
 * or prod host. No trailing slash. Bracket-style `process.env` for Vite/SSR tooling.
 *
 * When unset (typical dev), {@link canonicalPublicUrl} is a no-op.
 */
export function getSiteOrigin() {
  try {
    const env = globalThis.process?.env;
    const raw = String(env?.["SITE_ORIGIN"] ?? env?.["PUBLIC_SITE_ORIGIN"] ?? "").trim();
    if (!raw) return "";
    let normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    normalized = upgradeHttpToHttpsInProduction(normalized);
    normalized = normalized.replace(/\/$/, "");
    try {
      return new URL(normalized).origin;
    } catch {
      return "";
    }
  } catch {
    return "";
  }
}

/**
 * Canonical URLs, og:url, and hreflang `href`s: swaps build-time localhost (or merges site-relative
 * paths) using {@link getSiteOrigin} when set.
 * @param {string} urlLike Absolute URL or site path (`/…`).
 */
export function canonicalPublicUrl(urlLike) {
  const step = normalizePageAbsoluteUrl(String(urlLike ?? "").trim());
  const base = getSiteOrigin();
  if (!base) return step;
  try {
    if (step.startsWith("/")) {
      const o = base.replace(/\/$/, "");
      return o + step;
    }
    const incoming = new URL(step);
    const o = new URL(base);
    return o.origin + incoming.pathname + incoming.search + incoming.hash;
  } catch {
    return step;
  }
}

/**
 * Favicon and default OG/Twitter images: same CDN as fonts when `ASSET_CDN_BASE` is set,
 * so HTML never hits origin `/img/...` (which may 302 to an insecure URL on some CDNs).
 * @param {string} pathOrUrl site path (`/img/...`) or absolute URL
 * @returns {string}
 */
export function resolveHeadMetaAssetUrl(pathOrUrl) {
  const s = String(pathOrUrl || "").trim();
  if (!s) return s;
  if (s.startsWith("https://") || s.startsWith("data:")) return s;
  if (s.startsWith("http://")) return upgradeHttpToHttpsInProduction(s);
  if (s.startsWith("/")) return getAssetCdnBase() ? toAbsoluteCdnPath(s) : s;
  return s;
}

/**
 * When no `CDN_BASE_URL` / `ASSET_CDN_BASE` / `PUBLIC_CDN_BASE` is set at **build** time and `NODE_ENV` is
 * `production`, bake media URLs against the Content API pull-through CDN (same host as `CONTENT_API_URL`).
 * Override with `DEFAULT_MEDIA_CDN_BASE` or set `ASSET_CDN_BASE` / `CDN_BASE_URL` explicitly — required for
 * Bunny/deployed builds that must load from `https://cdn.lernlaterne.de`.
 */
const DEFAULT_LOCAL_BUILD_MEDIA_CDN = "http://localhost:8001/cdn";

/** Browser bundles without a CDN env banner can read SSR `#ll-asset-cdn-config`. */
function readShellAssetCdnBase() {
  try {
    if (typeof document === "undefined") return "";
    const el = document.getElementById("ll-asset-cdn-config");
    if (!el) return "";
    const j = JSON.parse(el.textContent || "{}");
    const base = String(j.base || "")
      .trim()
      .replace(/\/$/, "");
    if (!base || /^cdn$/i.test(base) || /^local$/i.test(base)) return "";
    return base;
  } catch {
    return "";
  }
}

function getRawAssetCdnEnv() {
  try {
    /** Bracket access: avoids Vite `define` / SSR transforms freezing `process.env.FOO` at build time. */
    const env = globalThis.process?.env;
    if (!env) return readShellAssetCdnBase();
    const raw = String(
      env["CDN_BASE_URL"] || env["ASSET_CDN_BASE"] || env["PUBLIC_CDN_BASE"] || "",
    )
      .trim()
      .replace(/\/$/, "");
    /**
     * Static `dist/` is generated at **build** time; there is no server env on a pull-zone host.
     * Local **production** builds (`npm run build` / `preview`) default to `http://localhost:8001/cdn` so
     * `/audio`, `/img`, … match dev (`scripts/dev.mjs`) and Content API mirroring. Bunny CI/images must set
     * `ASSET_CDN_BASE` (see `.github/workflows`, Dockerfile). `local` / `cdn` keep paths same-origin.
     */
    if (raw === "" && env.NODE_ENV === "production") {
      const override = String(env["DEFAULT_MEDIA_CDN_BASE"] ?? "").trim().replace(/\/$/, "");
      if (override) return override;
      const shellBase = readShellAssetCdnBase();
      if (shellBase) return shellBase;
      return DEFAULT_LOCAL_BUILD_MEDIA_CDN.replace(/\/$/, "");
    }
    if (raw === "") {
      const shellBase = readShellAssetCdnBase();
      if (shellBase) return shellBase;
    }
    return raw;
  } catch {
    return readShellAssetCdnBase();
  }
}

/**
 * `CDN_BASE_URL` (preferred for local CDN server) or `ASSET_CDN_BASE` = real pull-zone URL, e.g. `https://cdn.lernlaterne.de` (no trailing slash).
 * Use the literal `cdn` or `local` to keep URLs site-relative; serve assets from the local
 * `cdn/` and `data/content/` trees (identical to what you push to the pull zone after split).
 */
export function isLocalCdnPathMode() {
  const b = getRawAssetCdnEnv();
  if (!b) return true;
  return /^cdn$/i.test(b) || /^local$/i.test(b);
}

/**
 * When set (e.g. `https://cdn.lernlaterne.de`, no trailing slash), public paths like
 * `/audio/...` and `/pdf/...` are turned into absolute URLs on that host.
 * Empty, `cdn`, or `local` → `""` (same-origin, files from `cdn/` + `data/content/` on disk).
 */
export function getAssetCdnBase() {
  if (isLocalCdnPathMode()) return "";
  return upgradeHttpToHttpsInProduction(getRawAssetCdnEnv());
}

/**
 * True when `ASSET_CDN_BASE` is a deployed HTTPS (or http) host — we trust missing local
 * files to exist on that CDN and resolve conventional paths (scene art, etc.).
 */
export function isRemotePublicAssetCdn() {
  const b = getAssetCdnBase();
  return b.startsWith("https://") || b.startsWith("http://");
}

/**
 * Optional path segment *before* site paths, to keep new uploads separate from an existing
 * production site in the same storage zone, e.g. `cdn` → `https://host/cdn/audio/...`.
 * Set to empty after migration or if you use a dedicated storage zone and serve `/audio/...` at the origin root.
 */
export function getAssetCdnPathPrefix() {
  const raw = String(globalThis.process?.env?.ASSET_CDN_PATH_PREFIX || "").trim().replace(/^\/+|\/+$/g, "");
  return raw ? `/${raw}` : "";
}

/** Bunny / Content API CDN serves flat `img/stories/*` and `img/scenes/*` (no legacy `webp/` segment). */
export function normalizeFlatCdnMediaPath(publicPath) {
  const p = String(publicPath || "").trim();
  if (!p.startsWith("/")) return p;
  return p
    .replace(/^\/img\/stories\/webp\//, "/img/stories/")
    .replace(/^\/img\/scenes\/webp\//, "/img/scenes/");
}

/**
 * @param {string} publicPath
 * @returns {string}
 */
export function toAbsoluteCdnPath(publicPath) {
  let p = String(publicPath || "").trim();
  if (!p) return p;
  if (p.startsWith("https://") || p.startsWith("data:")) {
    return p;
  }
  if (p.startsWith("http://")) {
    return upgradeHttpToHttpsInProduction(p);
  }
  const base = getAssetCdnBase();
  if (!base) return appendDevMediaCacheBuster(p);
  p = normalizeFlatCdnMediaPath(p);
  const prefix = getAssetCdnPathPrefix();
  if (p.startsWith("/")) return appendDevMediaCacheBuster(base + prefix + p);
  return appendDevMediaCacheBuster(`${base}${prefix}/${p}`);
}

/**
 * Cera `@font-face` URLs: in local dev, serve from same-origin `public/fonts` (symlink)
 * instead of cross-origin Content API `:8001/cdn` — avoids CORS / missing API issues.
 * @param {string} publicPath site path under `/fonts/…`
 * @returns {string}
 */
export function toAbsoluteFontPath(publicPath) {
  const p = String(publicPath || "").trim();
  if (!p) return p;
  if (p.startsWith("https://") || p.startsWith("http://") || p.startsWith("data:")) {
    return p;
  }
  try {
    const env = globalThis.process?.env;
    const isDev = env?.NODE_ENV === "development" || env?.LL_DEV_FAST === "1";
    if (isDev && p.startsWith("/fonts/") && (regionalCeraSubsetFilesPresentInTree() || notoScriptSupplementFilesPresentInTree())) {
      return appendDevMediaCacheBuster(p);
    }
  } catch {
    /* browser bundle — fall through */
  }
  return toAbsoluteCdnPath(p);
}

/**
 * Content sometimes ships absolute URLs on the public pull zone. Remap that host to the current
 * {@link getAssetCdnBase} (e.g. localhost Content API `/cdn`) so local prod builds do not hit Bunny.
 *
 * Optional `PUBLIC_MEDIA_CDN_HOST` (no scheme), default `cdn.lernlaterne.de`.
 *
 * @param {string} urlLike
 * @returns {string} Site path `/…` or unchanged URL if host does not match.
 */
export function rewriteKnownPublicMediaUrl(urlLike) {
  const raw = String(urlLike || "").trim();
  if (!raw.startsWith("http://") && !raw.startsWith("https://")) return raw;
  try {
    const env = globalThis.process?.env;
    const configured = String(env?.PUBLIC_MEDIA_CDN_HOST ?? "cdn.lernlaterne.de")
      .trim()
      .replace(/^www\./i, "")
      .toLowerCase();
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    if (!configured || host !== configured) return raw;
    // Dev / builds with ASSET_CDN_BASE on the pull zone must keep absolute URLs (cache buster, <audio src>).
    const base = getAssetCdnBase();
    if (base) {
      try {
        const baseHost = new URL(base.endsWith("/") ? base : `${base}/`).hostname
          .replace(/^www\./i, "")
          .toLowerCase();
        if (baseHost === host) return raw;
      } catch {
        /* remap to site path below */
      }
    }
    return normalizeFlatCdnMediaPath(parsed.pathname + parsed.search + parsed.hash);
  } catch {
    return raw;
  }
}

/** HTTPS pages: coerce `http://` media URLs in production builds. */
export function finalizeMediaAbsoluteUrl(urlLike) {
  return upgradeHttpToHttpsInProduction(String(urlLike ?? "").trim());
}

/**
 * bunny.net pull zone: Dynamic Images (optimizer) – resize via query params.
 * @see https://docs.bunny.net/optimizer/dynamic-images/resizing
 * Only applied when the URL is served from `ASSET_CDN_BASE` (no-op for same-origin / dev).
 * @param {string} absoluteUrl
 * @param {{ width?: number, height?: number, quality?: number, aspectRatio?: string }} [opts]
 * @returns {string}
 */
export function appendBunnyImageParams(absoluteUrl, opts) {
  const urlStr = String(absoluteUrl || "").trim();
  if (!urlStr) return urlStr;
  const base = getAssetCdnBase();
  if (!base) return urlStr;
  if (!urlStr.startsWith(base)) return urlStr;
  const w = opts && opts.width;
  const h = opts && opts.height;
  const q = opts && opts.quality;
  const ar = opts && opts.aspectRatio;
  if (w == null && h == null && q == null && ar == null) return urlStr;
  try {
    const u = new URL(urlStr);
    if (/\.svg$/i.test(u.pathname)) return urlStr;
    // Bunny applies crops (aspect_ratio) before resizing (width/height).
    if (ar != null) u.searchParams.set("aspect_ratio", String(ar));
    if (w != null) u.searchParams.set("width", String(w));
    if (h != null) u.searchParams.set("height", String(h));
    if (q != null) u.searchParams.set("quality", String(q));
    return appendDevMediaCacheBuster(u.toString());
  } catch {
    return appendDevMediaCacheBuster(urlStr);
  }
}

/**
 * Remove Bunny optimizer query params so a new width/height can be applied (e.g. client layout).
 */
export function stripBunnyImageParams(absoluteUrl) {
  const urlStr = String(absoluteUrl || "").trim();
  if (!urlStr || urlStr.startsWith("/")) return urlStr;
  try {
    const u = new URL(urlStr);
    u.searchParams.delete("width");
    u.searchParams.delete("height");
    u.searchParams.delete("quality");
    const out = u.toString();
    return out.endsWith("?") ? out.slice(0, -1) : out;
  } catch {
    return urlStr;
  }
}

/**
 * @param {string} publicPath site path or absolute URL
 * @param {{ width?: number, height?: number, quality?: number }} [size]
 */
export function cdnSizedImagePath(publicPath, size) {
  return appendBunnyImageParams(toAbsoluteCdnPath(publicPath), size || {});
}

/**
 * White-bar lantern: the watercolor mark on a white square (not the black-bg “white” file).
 * Local dev serves same-origin `/img/…` so the logo still shows when the Content API is down.
 */
export function brandBarLanternSrc() {
  try {
    const env = globalThis.process?.env;
    const isDev = env?.NODE_ENV === "development" || env?.LL_DEV_FAST === "1";
    if (isDev) return "/img/index/laterne-square.png";
  } catch {
    /* browser bundle */
  }
  return cdnSizedImagePath("/img/index/laterne-square.png", CDN_IMG_LOGO_NAV);
}

/**
 * Map exercise illustration PNG site paths to WEBP siblings.
 * @param {string} publicPath e.g. `/img/stories/png/sto001/ending1.png`
 * @returns {string}
 */
export function exerciseIllustrationPngToWebpPath(publicPath) {
  const s = String(publicPath || "").trim();
  if (!s) return s;
  const m = s.match(/^(\/img\/(?:stories|scenes))\/png\/((?:sto|sce)\d+)\/([^/?#]+)\.png$/i);
  if (!m) return s;
  return `${m[1]}/${m[2]}/${m[3]}.webp`;
}

/**
 * Home/landing hero art (`lernlaterne-new.png`): responsive variants so mobile
 * doesn't download the fixed 1000px PNG. CSS: full container width ≤768px
 * (100vw − 32px), else half the 1100px container minus gap (≈494px max).
 * @param {string} publicPath
 */
export function homeHeroResponsiveUrls(publicPath) {
  const b = stripBunnyImageParams(toAbsoluteCdnPath(publicPath));
  const widths = [380, 494, 660, 760, 988, 1140];
  return {
    src: appendBunnyImageParams(b, { width: 660, quality: 75 }),
    srcSet: widths
      .map((w) => `${appendBunnyImageParams(b, { width: w, quality: w > 700 ? 62 : 75 })} ${w}w`)
      .join(", "),
    sizes: "(max-width: 768px) calc(100vw - 32px), min(50vw - 56px, 494px)",
  };
}

/**
 * Fixed square ending art: 1×/2×/3× physical widths for `srcset` (matches client DPR cap).
 * @param {string} base stripped CDN URL
 * @param {number} cssPx CSS layout box (108 btn, 200 illus)
 */
function endingSquareResponsiveUrls(base, cssPx) {
  const b = stripBunnyImageParams(String(base || "").trim());
  const px = Math.max(1, Math.round(Number(cssPx) || 1));
  if (!b) {
    return { src: "", srcSet: "", sizes: px + "px" };
  }
  const parts = [];
  for (const dpr of [1, 2, 3]) {
    const w = px * dpr;
    // Retina variants render downscaled — compress harder without visible loss.
    const quality = dpr >= 3 ? 55 : dpr >= 2 ? 62 : 75;
    parts.push(`${appendBunnyImageParams(b, { width: w, height: w, quality })} ${w}w`);
  }
  return {
    src: appendBunnyImageParams(b, { width: px * 2, height: px * 2, quality: 62 }),
    srcSet: parts.join(", "),
    sizes: px + "px",
  };
}

/**
 * @param {string} publicPath
 * @returns {{
 *   src: string,
 *   btnSrc: string,
 *   illusSrc: string,
 *   btnSrcSet: string,
 *   illusSrcSet: string,
 *   btnSizes: string,
 *   illusSizes: string,
 *   btnWidth: number,
 *   btnHeight: number,
 *   illusWidth: number,
 *   illusHeight: number,
 * }}
 */
function localEndingWebpFallback(flatPath) {
  if (getAssetCdnBase()) return flatPath;
  const m = String(flatPath || "").match(/^(\/img\/(?:stories|scenes)\/)((?:sto|sce)\d+\/[^/?#]+\.webp)$/i);
  if (!m) return flatPath;
  const nested = `${m[1]}webp/${m[2]}`;
  try {
    const proc = globalThis.process;
    if (!proc || typeof proc.cwd !== "function" || typeof proc.getBuiltinModule !== "function") return flatPath;
    const fs = proc.getBuiltinModule("node:fs");
    const path = proc.getBuiltinModule("node:path");
    if (!fs?.existsSync || !path?.join) return flatPath;
    const rel = nested.replace(/^\/+/, "");
    const found = ["public", "cdn", "data/content"].some((dir) => fs.existsSync(path.join(proc.cwd(), dir, rel)));
    return found ? nested : flatPath;
  } catch {
    return flatPath;
  }
}

export function resolveEndingImage(publicPath) {
  const webp = localEndingWebpFallback(exerciseIllustrationPngToWebpPath(publicPath));
  const base = stripBunnyImageParams(toAbsoluteCdnPath(webp));
  const btn = endingSquareResponsiveUrls(base, ENDING_BTN_LAYOUT.width);
  const illus = endingSquareResponsiveUrls(base, ENDING_ILLUS_LAYOUT.width);
  return {
    src: base,
    btnSrc: btn.src,
    illusSrc: illus.src,
    btnSrcSet: btn.srcSet,
    illusSrcSet: illus.srcSet,
    btnSizes: btn.sizes,
    illusSizes: illus.sizes,
    btnWidth: ENDING_BTN_LAYOUT.width,
    btnHeight: ENDING_BTN_LAYOUT.height,
    illusWidth: ENDING_ILLUS_LAYOUT.width,
    illusHeight: ENDING_ILLUS_LAYOUT.height,
  };
}

/**
 * bunny.net: bounding box for list / card thumbnails (fit inside width×height).
 * Must match CSS layout aspect ratio or the optimizer crops wrong vs `object-fit: cover`.
 * Adventure + scene cards: 21/9 (see `.card--adventure`, `.adventure-scene-card`). Story list + detail: 21/9.
 * Story/dialog list + reader hero use {@link ./responsive-cover-image-layout.js} (`srcset`/`sizes`).
 * Legacy cards may still use these defaults until migrated.
 */
export const CDN_IMG_ADVENTURE_CARD = { width: 1260, height: 540 };
export const CDN_IMG_STORY_CARD = { width: 840, height: 360 };
/** @deprecated Prefer {@link buildStoryListCoverImage} from `./responsive-story-images.js`. */
export const CDN_IMG_STORY_COVER = { width: 1260, height: 540 };
export const CDN_IMG_DIALOG_CARD = { width: 840, height: 360 };
/** Home hero: must match `.hero__image` (aspect-ratio 4/3). ~500px column ×2 DPR → 1000w. */
export const CDN_IMG_HOME_HERO = { width: 1000, height: 750 };
/** Target-language landing hero (4:3). */
export const CDN_IMG_TARGET_LANDING = { width: 800, height: 600 };
/** About block portrait: must match `.about-section__image` (aspect-ratio 3/4). */
export const CDN_IMG_HOME_AVATAR = { width: 800, height: 1067 };
export const CDN_IMG_SCENE_FULL = { width: 2048, height: 2048 };
export const CDN_IMG_LOGO_NAV = { width: 64, height: 64 };
/** Login panel lantern (square mark); matches `.ll-login-lantern__img` 96×96 CSS. */
export const CDN_IMG_LOGIN_LANTERN = { width: 96, height: 96 };
export const CDN_IMG_OG = { width: 1200, height: 630 };
/** In-game: inventory / small item strip */
export const CDN_IMG_ADVENTURE_ITEM = { width: 200, height: 200 };
/** Story ending CDN slot (`.ll-ending-illus` 200px; same file for choice buttons). */
export const ENDING_IMAGE_CDN_CSS = { width: 200, height: 200 };
/** Story ending choice button layout (`.ll-ending-btn__media` 108×108). */
export const ENDING_BTN_LAYOUT = { width: 108, height: 108 };
/** Story ending inline illustration layout (`.ll-ending-illus` 200×200). */
export const ENDING_ILLUS_LAYOUT = { width: 200, height: 200 };
/** In-game: infobox (interactable / item) art */
export const CDN_IMG_ADVENTURE_INFO = { width: 560, height: 640 };
/** In-game: dialog / convention portrait column (2:3) */
export const CDN_IMG_ADVENTURE_AVATAR = { width: 400, height: 600 };

/**
 * og:image / twitter:image: same as {@link resolveHeadMetaAssetUrl} but with optimizer bounds when on CDN.
 * @param {string} pathOrUrl
 * @param {{ width?: number, height?: number }} [size]
 * @returns {string}
 */
export function resolveHeadMetaImageUrl(pathOrUrl, size) {
  return appendBunnyImageParams(resolveHeadMetaAssetUrl(pathOrUrl), size || CDN_IMG_OG);
}

/**
 * Regional subset files from `npm run fonts:build-regional` (served at **`/fonts/regional/…`** on CDN).
 * @type {{ file: string, weight: number, style: string, unicodeRange: string }[]}
 */
const CERA_WOFF2_REGIONAL_LATIN = [
  {
    file: "cera-light-latin.woff2",
    weight: 300,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_LATIN,
  },
  {
    file: "cera-regular-latin.woff2",
    weight: 400,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_LATIN,
  },
  {
    file: "cera-regular-italic-latin.woff2",
    weight: 400,
    style: "italic",
    unicodeRange: UNICODE_RANGE_REGIONAL_LATIN,
  },
  {
    file: "cera-medium-latin.woff2",
    weight: 500,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_LATIN,
  },
  {
    file: "cera-bold-latin.woff2",
    weight: 700,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_LATIN,
  },
];

/** Deferred on `/vi/` — tone-marked Vietnamese letters not in the European Latin subset. */
const CERA_WOFF2_REGIONAL_VIETNAMESE = [
  {
    file: "cera-light-vietnamese.woff2",
    weight: 300,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_VIETNAMESE,
  },
  {
    file: "cera-regular-vietnamese.woff2",
    weight: 400,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_VIETNAMESE,
  },
  {
    file: "cera-regular-italic-vietnamese.woff2",
    weight: 400,
    style: "italic",
    unicodeRange: UNICODE_RANGE_REGIONAL_VIETNAMESE,
  },
  {
    file: "cera-medium-vietnamese.woff2",
    weight: 500,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_VIETNAMESE,
  },
  {
    file: "cera-bold-vietnamese.woff2",
    weight: 700,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_VIETNAMESE,
  },
];

/** Deferred on `/uk/` and `/ru/`. */
const CERA_WOFF2_REGIONAL_CYRILLIC = [
  {
    file: "cera-light-cyrillic.woff2",
    weight: 300,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_CYRILLIC,
  },
  {
    file: "cera-regular-cyrillic.woff2",
    weight: 400,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_CYRILLIC,
  },
  {
    file: "cera-regular-italic-cyrillic.woff2",
    weight: 400,
    style: "italic",
    unicodeRange: UNICODE_RANGE_REGIONAL_CYRILLIC,
  },
  {
    file: "cera-medium-cyrillic.woff2",
    weight: 500,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_CYRILLIC,
  },
  {
    file: "cera-bold-cyrillic.woff2",
    weight: 700,
    style: "normal",
    unicodeRange: UNICODE_RANGE_REGIONAL_CYRILLIC,
  },
];

const CERA_WOFF2_REGIONAL_GREEK = CERA_WOFF2_REGIONAL_CYRILLIC.map((row) => ({
  ...row,
  file: row.file.replace("-cyrillic", "-greek"),
  unicodeRange: UNICODE_RANGE_REGIONAL_GREEK,
}));

/**
 * `data/content/fonts/regional/cera-bold-latin.woff2` (or legacy flat **`cera-bold-latin.woff2`**) activates regional faces.
 * Resolved via `ll-regional-font-sentinel` (SSR uses `node:fs`; browser stub returns false).
 */
export { regionalCeraSubsetFilesPresentInTree, notoScriptSupplementFilesPresentInTree, notoCjkStylesheetPresentInTree };

/**
 * Noto `@font-face` for Arabic + Vietnamese — same `font-family` as Cera so layout stays unchanged.
 * @param {{ file: string, weight: number, style: string, unicodeRange?: string }[]} rows
 * @param {(p: string) => string} urlForPath
 */
function buildNotoSupplementDescriptorsCss(rows, urlForPath) {
  return rows
    .map((f) => {
      const ur =
        typeof f.unicodeRange === "string" && f.unicodeRange.trim()
          ? `  unicode-range: ${f.unicodeRange};\n`
          : "";
      return `@font-face {
  font-family: "Cera Pro";${ur ? `\n${ur}` : "\n"}
  src: url('${urlForPath(`/fonts/noto/${f.file}`)}') format("woff2");
  font-weight: ${f.weight};
  font-style: ${f.style};
  font-display: swap;
}`;
    })
    .join("\n");
}

/** Noto Sans UI family for explicit Vietnamese copy (title-aid, teasers). */
function buildNotoSansUiFontFaceRulesCss(urlForPath = toAbsoluteFontPath) {
  if (!notoScriptSupplementFilesPresentInTree()) return "";
  return notoSansUiFontFaceRows()
    .map((f) => {
      const ur =
        typeof f.unicodeRange === "string" && f.unicodeRange.trim()
          ? `  unicode-range: ${f.unicodeRange};\n`
          : "";
      return `@font-face {
  font-family: "Noto Sans";${ur ? `\n${ur}` : "\n"}
  src: url('${urlForPath(`/fonts/noto/${f.file}`)}') format("woff2");
  font-weight: ${f.weight};
  font-style: ${f.style};
  font-display: swap;
}`;
    })
    .join("\n");
}

function buildNotoScriptSupplementRulesCss(urlForPath = toAbsoluteFontPath) {
  if (!notoScriptSupplementFilesPresentInTree()) return "";
  return buildNotoSupplementDescriptorsCss(notoScriptSupplementFontFaceRows(), urlForPath);
}

function supplementUsesNoto(key) {
  return (key === "vietnamese" || key === "arabic") && notoScriptSupplementFilesPresentInTree();
}

/**
 * @param {{ file: string, weight: number, style: string, unicodeRange?: string }[]} rows
 * @param {(p: string) => string} urlForPath
 */
/**
 * @param {{ file: string, weight: number, style: string, unicodeRange?: string }[]} rows
 * @param {(p: string) => string} urlForPath
 * @param {{ fontDisplay?: string }} [opts]
 */
function buildCeraDescriptorsCss(rows, urlForPath, opts = {}) {
  const fontDisplay = opts.fontDisplay || "swap";
  return rows
    .map((f) => {
      const ur =
        typeof f.unicodeRange === "string" && f.unicodeRange.trim()
          ? `  unicode-range: ${f.unicodeRange};\n`
          : "";
      return `@font-face {
  font-family: "Cera Pro";${ur ? `\n${ur}` : "\n"}
  src: url('${urlForPath(`/fonts/regional/${f.file}`)}') format("woff2");
  font-weight: ${f.weight};
  font-style: ${f.style};
  font-display: ${fontDisplay};
}`;
    })
    .join("\n");
}

/**
 * Marketing shell: bold is already inlined as `data:` subsets — skip external **`cera-bold*.woff2`**
 * in blocking `@font-face` to avoid redundant downloads.
 *
 * @param {{ file: string, weight: number, style: string, unicodeRange?: string }[]} rows
 */
function filterDescriptorsExcludingExternalBoldForMarketingInline(rows) {
  return rows.filter((f) => !(f.weight === 700 && f.style === "normal"));
}

/**
 * Reader first paint: Regular + Bold (titles/nav use 700; matches
 * {@link getCeraLatinReaderPreloadLinkTags}).
 */
function filterDescriptorsReaderPrimaryOnly(rows) {
  return rows.filter(
    (f) => f.style === "normal" && (f.weight === 400 || f.weight === 700),
  );
}

/** Deferred on reader: medium / light / italic (not needed for first paint). */
function filterDescriptorsReaderDeferredRest(rows) {
  return rows.filter(
    (f) => !(f.style === "normal" && (f.weight === 400 || f.weight === 700)),
  );
}

/**
 * Same as {@link buildCeraProFontFaceRulesCss} but omits weight **700** (covered by inlined bold on marketing pages).
 */
function buildCeraMarketingRestFontFaceRulesCss(urlForPath = toAbsoluteFontPath) {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  return buildCeraDescriptorsCss(
    filterDescriptorsExcludingExternalBoldForMarketingInline(CERA_WOFF2_REGIONAL_LATIN),
    urlForPath,
  );
}

/**
 * Metric-adjusted local fallbacks for Cera Pro (Lighthouse font-swap CLS fix).
 *
 * While the Cera WOFF2s load, text renders in Roboto (Android) or Arial/Helvetica
 * (desktop). Without overrides the fallback wraps differently and the whole reader
 * column reflows on swap (CLS ≈ 0.3 on story pages). `size-adjust` matches average
 * German glyph width to Cera; ascent/descent/line-gap match Cera's vertical metrics.
 * Values computed from the font files via fontTools (see repo scripts history):
 *   Cera upm 1000, ascent 808, descent 192, lineGap 257.
 * `--font-sans` must list these families between "Cera Pro" and system-ui.
 * @returns {string}
 */
export function buildCeraFallbackFontFaceRulesCss() {
  return `@font-face {
  font-family: "Cera Pro Fallback Roboto";
  src: local("Roboto");
  size-adjust: 103.36%;
  ascent-override: 78.17%;
  descent-override: 18.58%;
  line-gap-override: 24.86%;
}
@font-face {
  font-family: "Cera Pro Fallback Arial";
  src: local("Arial"), local("Helvetica Neue"), local("Liberation Sans");
  size-adjust: 102.53%;
  ascent-override: 78.80%;
  descent-override: 18.73%;
  line-gap-override: 25.07%;
}`;
}

/**
 * External Cera `@font-face` rules — Latin regional subsets via `npm run fonts:build-regional`
 * (**`data/content/fonts/regional/cera-bold-latin.woff2`** sentinel, or legacy flat file).
 * Omits oversized monolithic WOFF2; when subsets are absent the tag is empty until assets are installed.
 * @param {(p: string) => string} urlForPath
 * @returns {string}
 */
export function buildCeraProFontFaceRulesCss(urlForPath = toAbsoluteFontPath) {
  if (!regionalCeraSubsetFilesPresentInTree() && !notoScriptSupplementFilesPresentInTree()) return "";
  const parts = [];
  if (regionalCeraSubsetFilesPresentInTree()) {
    parts.push(buildCeraDescriptorsCss(CERA_WOFF2_REGIONAL_LATIN, urlForPath));
  }
  parts.push(buildCeraFallbackFontFaceRulesCss());
  return parts.filter(Boolean).join("\n");
}

/**
 * Favicon: prefer asset CDN when configured (avoids bad HTTP redirects on some pull zones).
 * @returns {string}
 */
export function getFaviconHref() {
  return resolveHeadMetaAssetUrl("/img/index/favicon.ico");
}

/**
 * Inline `<style>` for Cera Pro @font-face. URLs respect ASSET_CDN_BASE / path prefix; otherwise /fonts/... (e.g. public symlink).
 * @returns {string}
 */
/**
 * Noto faces for every script outside Latin, Greek and Cyrillic, on every page.
 * unicode-range keeps the files from downloading until those characters are painted.
 * @returns {string}
 */
export function getNotoScriptCoverageStyleTagHtml() {
  const css = buildNotoScriptSupplementRulesCss(toAbsoluteFontPath).trim();
  if (!css) return "";
  return `<style data-ll-noto-script-coverage="">\n${css}\n</style>`;
}

/**
 * Sliced Noto Sans CJK. The stylesheet is added only when the page contains those characters,
 * and each slice downloads only for the characters actually painted.
 */
export function getNotoCjkStylesheetLinkHtml() {
  if (!notoCjkStylesheetPresentInTree()) return "";
  const href = JSON.stringify(toAbsoluteFontPath("/fonts/noto/cjk.css"));
  return `<script data-ll-noto-cjk="">
(function(){
  function arm(){
    var text="";
    try{ text=document.body?document.body.textContent:""; }catch(e){}
    if(!/[\\u1100-\\u11FF\\u3040-\\u30FF\\u3130-\\u318F\\u3400-\\u9FFF\\uAC00-\\uD7AF]/.test(text))return;
    if(document.querySelector("link[data-ll-noto-cjk]"))return;
    var link=document.createElement("link");
    link.rel="stylesheet";
    link.href=${href};
    link.setAttribute("data-ll-noto-cjk","");
    document.head.appendChild(link);
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",arm,{once:true});
  else arm();
})();
</script>`;
}

/**
 * Greek and Cyrillic Cera faces on every page. unicode-range downloads a file
 * only when those characters are painted, including script courses whose
 * interface language is English or German.
 * @returns {string}
 */
export function getCeraGreekCyrillicStyleTagHtml() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const css = buildCeraDescriptorsCss(
    [...CERA_WOFF2_REGIONAL_GREEK, ...CERA_WOFF2_REGIONAL_CYRILLIC],
    toAbsoluteFontPath,
  ).trim();
  if (!css) return "";
  return `<style data-ll-cera-greek-cyrillic="">\n${css}\n</style>`;
}

export function getFontFaceStyleTagHtml() {
  const css = buildCeraProFontFaceRulesCss(toAbsoluteFontPath).trim();
  if (!css) return "";
  return `<style>\n${css}\n</style>`;
}

/**
 * Home (+ German list hubs) when the handler inlined `.generated/cera-*-critical.css` subsets:
 * only the **inlined** `data:` subset `@font-face` rules. Remaining weights:
 * {@link getBlockingCeraMarketingRestFacesStyleHtml} (no external **700** — bold is inlined),
 * {@link getCeraLatinPrimaryPreloadLinkTags} (regular + medium only),
 * {@link getDeferredMarketingBoldFontFaceScriptHtml} (idle load of full bold for the next navigation).
 *
 * Falls back to {@link getFontFaceStyleTagHtml} if `criticalInlineCss` is empty.
 * @param {string | null | undefined} criticalInlineCss
 * @returns {string}
 */
export function getHeadFontHtmlForHomePage(criticalInlineCss) {
  const critical = criticalInlineCss && String(criticalInlineCss).trim();
  if (!critical) {
    return getFontFaceStyleTagHtml();
  }
  return `<style>\n${critical}\n${buildCeraFallbackFontFaceRulesCss()}\n</style>`;
}

/**
 * After inlined bold subsets: load **remaining** Cera weights synchronously (no external **700** in this block).
 * {@link getDeferredMarketingBoldFontFaceScriptHtml} loads full bold WOFF2 after **`load` + idle**.
 *
 * Cyrillic/Greek/Vietnamese: {@link getCeraRegionalSupplementStyleTagHtml} + {@link getDeferredCeraRegionalSupplementScriptHtml}.
 * @returns {string}
 */
export function getBlockingCeraMarketingRestFacesStyleHtml() {
  const inner = buildCeraMarketingRestFontFaceRulesCss(toAbsoluteFontPath).trim();
  if (!inner) return "";
  return `<style data-ll-cera-blocking-rest="">\n${inner}\n</style>`;
}

/** Preload Regular + Medium; bold is **`getDeferredMarketingBoldFontFaceScriptHtml`** (idle after load). */
export function getCeraLatinPrimaryPreloadLinkTags() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const r = escapeAttr(toAbsoluteFontPath("/fonts/regional/cera-regular-latin.woff2"));
  const md = escapeAttr(toAbsoluteFontPath("/fonts/regional/cera-medium-latin.woff2"));
  return (
    `<link rel="preload" href="${r}" as="font" type="font/woff2" crossorigin="anonymous">\n` +
    `<link rel="preload" href="${md}" as="font" type="font/woff2" crossorigin="anonymous">\n`
  );
}

/**
 * Reader pages: preload Regular + Bold (titles/nav use 700). Medium ATF chrome
 * CLS is avoided via critical badge/layout CSS + metric-adjusted fallbacks.
 * @returns {string}
 */
export function getCeraLatinReaderPreloadLinkTags() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const r = escapeAttr(toAbsoluteFontPath("/fonts/regional/cera-regular-latin.woff2"));
  const b = escapeAttr(toAbsoluteFontPath("/fonts/regional/cera-bold-latin.woff2"));
  return (
    `<link rel="preload" href="${r}" as="font" type="font/woff2" crossorigin="anonymous">\n` +
    `<link rel="preload" href="${b}" as="font" type="font/woff2" crossorigin="anonymous">\n`
  );
}

/**
 * Reader: blocking Regular + Bold (+ metric fallbacks). Medium/italic/light via
 * {@link getDeferredCeraReaderRestFacesScriptHtml}.
 * @returns {string}
 */
export function getBlockingCeraReaderPrimaryFacesStyleHtml() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const faces = buildCeraDescriptorsCss(
    filterDescriptorsReaderPrimaryOnly(CERA_WOFF2_REGIONAL_LATIN),
    toAbsoluteFontPath,
  ).trim();
  if (!faces) return "";
  return `<style data-ll-cera-reader-primary="">\n${faces}\n${buildCeraFallbackFontFaceRulesCss()}\n</style>`;
}

/**
 * Idle after first paint window: register remaining Latin faces (medium / italic / light).
 * Uses font-display:swap so late weights still apply once loaded.
 * @returns {string}
 */
export function getDeferredCeraReaderRestFacesScriptHtml() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const rest = filterDescriptorsReaderDeferredRest(CERA_WOFF2_REGIONAL_LATIN);
  if (!rest.length) return "";
  const css = buildCeraDescriptorsCss(rest, toAbsoluteFontPath, {
    fontDisplay: "swap",
  }).trim();
  if (!css) return "";
  const cssJs = JSON.stringify(css);
  return `<script data-ll-deferred-cera-reader-rest="">
(function(){
  var css=${cssJs};
  function inject(){
    if(document.querySelector('style[data-ll-cera-deferred-reader-rest=""]'))return;
    var s=document.createElement("style");
    s.setAttribute("data-ll-cera-deferred-reader-rest","");
    s.textContent=css;
    document.head.appendChild(s);
  }
  function go(){
    if(typeof requestIdleCallback==="function")requestIdleCallback(inject,{timeout:8e3});
    else setTimeout(inject,2500);
  }
  // Wait past first paint + capability CSS inject before advertising extra faces.
  function afterPaint(){
    requestAnimationFrame(function(){requestAnimationFrame(function(){setTimeout(go,1500)})});
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",afterPaint);
  else afterPaint();
})();
</script>`;
}

/** @param {string} v */
function escapeAttr(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;");
}

/**
 * Idle after **`load`**: register **external bold** (`cera-bold*` WOFF2) so HTTP cache fills before the next
 * visit to a page **without** inlined bold · does not block first paint.
 *
 * @returns {string}
 */
export function getDeferredMarketingBoldFontFaceScriptHtml() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const boldRow =
    CERA_WOFF2_REGIONAL_LATIN.find((f) => f.weight === 700 && f.style === "normal") || null;
  if (!boldRow) return "";
  const url = toAbsoluteFontPath;
  const css = buildCeraDescriptorsCss([boldRow], url).trim();
  const cssJs = JSON.stringify(css);
  return `<script data-ll-deferred-cera-marketing-bold="">
(function(){
  var css=${cssJs};
  function inject(){
    if(document.querySelector('style[data-ll-cera-deferred-bold=""]'))return;
    var s=document.createElement("style");
    s.setAttribute("data-ll-cera-deferred-bold","");
    s.textContent=css;
    document.head.appendChild(s);
  }
  function go(){
    if(typeof requestIdleCallback==="function")requestIdleCallback(inject,{timeout:5e3});
    else setTimeout(inject,1);
  }
  if(document.readyState==="complete")go();
  else window.addEventListener("load",go);
})();
</script>`;
}

/**
 * Blocking `@font-face` for regional supplements on first paint (`/vi/`, `/uk/`, `/ru/`, `/el/`).
 * @param {string} [uiLang]
 * @returns {string}
 */
export function getCeraRegionalSupplementStyleTagHtml(uiLang) {
  const key = ceraRegionalSupplementKeyForUiLang(uiLang);
  if (!key) return "";
  const url = toAbsoluteFontPath;
  if (supplementUsesNoto(key)) {
    // The Cera Pro unicode-range faces are on every page. `/vi/` also needs the "Noto Sans" family for title-aid copy.
    if (key !== "vietnamese") return "";
    const notoSansUi = buildNotoSansUiFontFaceRulesCss(url).trim();
    if (!notoSansUi) return "";
    return `<style data-ll-noto-supplement="vietnamese-ui">\n${notoSansUi}\n</style>`;
  }
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  /** @type {{ file: string, weight: number, style: string, unicodeRange?: string }[]} */
  let rows = [];
  if (key === "vietnamese") rows = CERA_WOFF2_REGIONAL_VIETNAMESE;
  else if (key === "cyrillic") rows = CERA_WOFF2_REGIONAL_CYRILLIC;
  else if (key === "greek") rows = CERA_WOFF2_REGIONAL_GREEK;
  const css = buildCeraDescriptorsCss(rows, url).trim();
  if (!css) return "";
  return `<style data-ll-cera-supplement="${key}">\n${css}\n</style>`;
}

/**
 * Cyrillic and Greek Cera cuts, after first paint, on `/uk/`, `/ru/` and `/el/`.
 * Other scripts are Noto and ship with every page.
 * @returns {string}
 */
export function getDeferredCeraRegionalSupplementScriptHtml() {
  if (!regionalCeraSubsetFilesPresentInTree()) return "";
  const url = toAbsoluteFontPath;
  const cssCyr = buildCeraDescriptorsCss(CERA_WOFF2_REGIONAL_CYRILLIC, url).trim();
  const cssGre = buildCeraDescriptorsCss(CERA_WOFF2_REGIONAL_GREEK, url).trim();
  const cyrJs = JSON.stringify(cssCyr);
  const greJs = JSON.stringify(cssGre);

  return `<script data-ll-deferred-cera-regional-supplement="">
(function(){
  var cssCyrillic=${cyrJs};
  var cssGreek=${greJs};
  function pathLang(){
    try{
      var p=location.pathname||"";
      var seg=(p.replace(/^[/]+/,"").split("/")[0]||"").toLowerCase();
      return seg;
    }catch(e){ return ""; }
  }
  function docLang(){
    try{
      var lang=document.documentElement&&document.documentElement.getAttribute("lang");
      return String(lang||"").trim().toLowerCase();
    }catch(e){ return ""; }
  }
  function needsCyrillic(){
    var seg=pathLang();
    if(seg==="uk"||seg==="ru")return true;
    var lang=docLang();
    return lang==="uk"||lang.indexOf("uk-")===0||lang==="ru"||lang.indexOf("ru-")===0;
  }
  function needsGreek(){
    var seg=pathLang();
    if(seg==="el")return true;
    var lang=docLang();
    return lang==="el"||lang.indexOf("el-")===0;
  }
  function inject(key,css){
    if(!css)return;
    if(document.querySelector('style[data-ll-cera-supplement="'+key+'"]'))return;
    var el=document.createElement("style");
    el.setAttribute("data-ll-cera-supplement",key);
    el.textContent=css;
    document.head.appendChild(el);
  }
  function run(){
    if(needsCyrillic())inject("cyrillic",cssCyrillic);
    if(needsGreek())inject("greek",cssGreek);
  }
  function schedule(){
    if(!(needsCyrillic()||needsGreek()))return;
    if(document.readyState==="loading"){
      document.addEventListener("DOMContentLoaded",run,{once:true});
      return;
    }
    run();
  }
  schedule();
})();
</script>`;
}

/**
 * Kept for compatibility; marketing shell now uses {@link getBlockingCeraMarketingRestFacesStyleHtml}.
 * @returns {string}
 */
export function getDeferredCeraFullFontFaceScriptHtml() {
  return "";
}
